import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight, PhoneCall, ChevronRight, Sun } from 'lucide-react';
import { NavLink, Link, useLocation } from 'react-router-dom';

import logoImg from './assets/devdeepam_logo1.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Scroll shadow effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 15);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ✅ FIX 1: Close drawer ONLY when route changes (not when isOpen changes)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // ✅ FIX 2: Lock body scroll when drawer is open (separate effect)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/servicespage' },
    { name: 'Projects', path: '/projectpage' },
    { name: 'Solar Calculator', path: '/calculator' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      {/* ═══════════════ TOP NAVBAR ═══════════════ */}
      <motion.nav
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
          isScrolled
            ? 'shadow-[0_8px_30px_rgba(13,63,127,0.08)] border-b border-slate-100 py-3'
            : 'py-4 sm:py-5 border-b border-slate-100/50'
        }`}
      >
        {/* Brand top line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0D3F7F] via-[#1E5CB3] to-[#F7941D]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">

            {/* LOGO */}
            <Link
              to="/"
              className="flex items-center group relative z-50 transition-transform duration-300 active:scale-95"
            >
              <div className="h-10 sm:h-12 lg:h-14 w-auto flex items-center">
                <img
                  src={logoImg}
                  alt="DevDeepam Solar Pvt. Ltd."
                  className="h-full w-auto object-contain transition-all duration-300 group-hover:brightness-110"
                />
              </div>
            </Link>

            {/* DESKTOP LINKS */}
            <div className="hidden lg:flex items-center space-x-1 bg-slate-50/80 p-1.5 rounded-full border border-slate-200/50 shadow-sm backdrop-blur-sm">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <NavLink
                    key={link.name}
                    to={link.path}
                    className={`relative px-5 py-2 rounded-full text-[13px] font-semibold tracking-wide transition-all duration-300 flex items-center gap-1.5 ${
                      isActive
                        ? 'text-white'
                        : 'text-slate-600 hover:text-[#F7941D] hover:bg-white'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeTabPill"
                        className="absolute inset-0 bg-gradient-to-r from-[#0D3F7F] to-[#1E5CB3] rounded-full shadow-md shadow-[#0D3F7F]/20"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10">{link.name}</span>
                  </NavLink>
                );
              })}
            </div>

            {/* DESKTOP CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                to="/contact"
                className="group relative inline-flex items-center justify-center overflow-hidden rounded-full p-[2px] font-bold transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-[#F7941D] via-[#FFC107] to-[#F7941D] rounded-full opacity-70 blur-md group-hover:opacity-100 transition-opacity animate-pulse" />
                <span className="relative flex items-center gap-2 bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white text-[13px] font-bold px-6 py-2.5 rounded-full tracking-wide shadow-inner border border-white/20">
                  <span>Get Free Quote</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </div>

            {/* MOBILE HAMBURGER */}
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setIsOpen(true)}
                className="p-2.5 rounded-full bg-[#F0F7FF] border border-[#0D3F7F]/10 text-[#0D3F7F] hover:bg-[#F7941D] hover:text-white active:scale-95 transition-all focus:outline-none shadow-sm"
                aria-label="Open Menu"
              >
                <Menu className="w-5 h-5 stroke-[2.5]" />
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* ═══════════════ MOBILE DRAWER ═══════════════ */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[90] bg-[#0F172A]/70 backdrop-blur-sm lg:hidden"
              onClick={() => setIsOpen(false)}
            />

            {/* Side Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 260 }}
              className="fixed top-0 right-0 bottom-0 z-[100] w-[85%] max-w-[340px] bg-white shadow-2xl flex flex-col lg:hidden"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-white shrink-0">
                <img
                  src={logoImg}
                  alt="DevDeepam Solar"
                  className="h-8 sm:h-10 w-auto object-contain"
                />
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-full bg-slate-100 border border-slate-200 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all active:scale-90"
                  aria-label="Close Menu"
                >
                  <X className="w-5 h-5 stroke-[2.5]" />
                </button>
              </div>

              {/* Links */}
              <div className="flex-1 overflow-y-auto p-5 space-y-2 bg-slate-50/50">
                <div className="flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase text-[#0D3F7F]/60 mb-4 px-1">
                  <Sun className="w-4 h-4 text-[#F7941D]" />
                  <span>Main Menu</span>
                </div>

                {navLinks.map((link, index) => {
                  const isActive = location.pathname === link.path;
                  return (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * index + 0.08 }}
                    >
                      <NavLink
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center justify-between px-4 py-4 rounded-xl font-semibold text-[14px] transition-all duration-200 ${
                          isActive
                            ? 'bg-gradient-to-r from-[#0D3F7F] to-[#1E5CB3] text-white shadow-md shadow-[#0D3F7F]/20'
                            : 'bg-white text-slate-700 border border-slate-100 hover:border-[#F7941D]/40 hover:text-[#F7941D] shadow-sm'
                        }`}
                      >
                        <span className="tracking-wide">{link.name}</span>
                        <ChevronRight
                          className={`w-4 h-4 ${
                            isActive ? 'text-white translate-x-0.5' : 'text-slate-400'
                          }`}
                        />
                      </NavLink>
                    </motion.div>
                  );
                })}
              </div>

              {/* Drawer Footer */}
              <div className="p-5 bg-white border-t border-slate-100 space-y-4 shrink-0">
                <a
                  href="tel:+919876543210"
                  className="bg-[#F0F7FF] p-4 rounded-2xl border border-[#0D3F7F]/10 flex items-center gap-3 hover:bg-[#E0F0FF] transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F7941D] to-[#FFC107] flex items-center justify-center text-white shrink-0">
                    <PhoneCall className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase font-bold text-slate-500">
                      Need Guidance?
                    </div>
                    <div className="text-[13px] font-bold text-[#0D3F7F]">
                      Talk to Solar Expert
                    </div>
                  </div>
                </a>

                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="w-full bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white text-center py-4 rounded-full font-bold text-[13px] uppercase tracking-wider flex items-center justify-center gap-2 shadow-[0_8px_20px_rgba(247,148,29,0.25)] active:scale-95 transition-transform"
                >
                  <span>Get Free Solar Quote</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;